import { Injectable } from '@angular/core';
import { AbstractControl, UntypedFormGroup } from '@angular/forms';

import { APP_VERSION } from '../build-info';

/**
 * Form telemetry (DEV-6453).
 *
 * Emits one fire-and-forget event per completed step (each successful
 * "Ga verder" click), plus action events (back, blocked clicks,
 * abandonment, post-submit buttons). Delivery uses navigator.sendBeacon so
 * it never blocks the UI or the network thread. Values are sent as entered
 * (DEV-7892); only internal `_` controls are left out.
 *
 * Joins with the WP submit log (DEV-6458) and backend request-validation
 * log (DEV-6459) via the form session id: `form_session_id` in the beacon
 * body, and the X-Form-Session-Id header FormSessionInterceptor adds to
 * every super-form REST call (DEV-7864).
 */
@Injectable( {
	providedIn: 'root'
} )
export class TelemetryService {

	private static readonly SESSION_STORAGE_KEY = 'sirelo_form_session_id';
	private static readonly SEQ_STORAGE_KEY = 'sirelo_form_event_seq';
	private static readonly ENDPOINT_PATH = '/wp-json/super-form/v1/form-telemetry';

	/**
	 * Top-level keys the central log collector reserves for itself (DEV-7864).
	 * WP rebuilds the collector event from allowlisted fields, so these would
	 * not get through anyway; never sending them keeps the body and the
	 * collector event the same shape.
	 */
	private static readonly RESERVED_KEYS: ReadonlySet<string> = new Set( [ 'type', 'ts', 'host', 'msg', '_truncated' ] );

	/**
	 * Most bytes of `values` WP keeps (RFTelemetry::VALUES_MAX_BYTES); it
	 * replaces larger values with `{_truncated: true}`. Dropping them here
	 * instead sets `values_omitted`, so the collector knows why they are empty
	 * (DEV-7864). Every other field is bounded, so this also keeps the body
	 * well under the collector's 32 KiB.
	 */
	private static readonly MAX_VALUES_BYTES = 8192;

	/** Most controls listed in `invalid_fields`; a whole invalid form can have many. */
	private static readonly MAX_INVALID_FIELDS = 50;

	/** Most validator keys listed per control in `invalid_reasons` (WP keeps 5). */
	private static readonly MAX_REASONS_PER_FIELD = 5;

	private static readonly ONCE_STORAGE_PREFIX = 'sirelo_form_once_';

	/** Fallback for firstInSession() when sessionStorage is unavailable. */
	private memoryOnce = new Set<string>();

	/** Fallback counter for when sessionStorage is unavailable. */
	private memorySeq = 0;

	/**
	 * Session-level context (attribution) added to every event. Registered by
	 * FormService, which owns the form controls it reads from; this service
	 * cannot inject FormService without a circular dependency.
	 */
	private contextProvider: ( () => Record<string, unknown> ) | null = null;

	/**
	 * Stable, per-tab UUID. Generated lazily on first call and persisted in
	 * sessionStorage so subsequent emits within the same tab share it.
	 */
	public getSessionId(): string {

		if( typeof window === 'undefined' ) {
			// SSR / non-browser context. Each call returns a fresh id —
			// telemetry will still work but won't join across calls.
			return this.generateUuid();
		}

		try {

			let id = window.sessionStorage.getItem( TelemetryService.SESSION_STORAGE_KEY );
			if( ! id ) {

				id = this.generateUuid();
				window.sessionStorage.setItem( TelemetryService.SESSION_STORAGE_KEY, id );
			}
			return id;

		} catch {

			// sessionStorage can throw in private browsing / disabled storage.
			return this.generateUuid();
		}
	}

	/**
	 * True the first time `key` is asked for in this form session, false after
	 * (DEV-7892 review): kept in sessionStorage next to the session id, so a
	 * reload in the same tab does not count as a new first time.
	 */
	public firstInSession( key: string ): boolean {

		const storageKey = TelemetryService.ONCE_STORAGE_PREFIX + key;

		try {

			if( window.sessionStorage.getItem( storageKey ) ) {
				return false;
			}

			window.sessionStorage.setItem( storageKey, '1' );
			return true;

		} catch {

			if( this.memoryOnce.has( key ) ) {
				return false;
			}

			this.memoryOnce.add( key );
			return true;
		}
	}

	public setContextProvider( provider: () => Record<string, unknown> ): void {

		this.contextProvider = provider;
	}

	/**
	 * Dotted paths of the invalid controls under `control` (`email`,
	 * `movingFrom.city`), capped at MAX_INVALID_FIELDS (DEV-7864). Names only,
	 * never values, so it is safe for PII fields.
	 */
	public invalidControlPaths( control: AbstractControl, prefix = '' ): string[] {

		const out: string[] = [];
		const children = ( control as UntypedFormGroup ).controls;

		if( ! children || ! control.invalid ) {
			return out;
		}

		for( const key of Object.keys( children ) ) {

			const child = children[ key ];
			if( ! child.invalid ) {
				continue;
			}

			const path = prefix ? prefix + '.' + key : key;
			const nested = this.invalidControlPaths( child, path );

			// A group that is invalid by its own validator has no invalid child; list the group.
			out.push( ...( nested.length > 0 ? nested : [ path ] ) );
		}

		return out.slice( 0, TelemetryService.MAX_INVALID_FIELDS );
	}

	/**
	 * The validator keys of each invalid control under `control`, keyed by the
	 * same dotted paths as invalidControlPaths() (DEV-7892), e.g.
	 * `{ 'nameEmailPhone.email': [ 'email' ] }`. At most MAX_REASONS_PER_FIELD
	 * keys per control.
	 */
	public invalidControlReasons( control: AbstractControl ): Record<string, string[]> {

		const out: Record<string, string[]> = {};

		for( const path of this.invalidControlPaths( control ) ) {

			const errors = control.get( path )?.errors;
			if( errors ) {
				out[ path ] = Object.keys( errors ).slice( 0, TelemetryService.MAX_REASONS_PER_FIELD );
			}
		}

		return out;
	}

	/**
	 * Build a payload from the *leaving* step's FormGroup and ship it via
	 * sendBeacon. Never throws.
	 *
	 * `durationMs` is this visit of the step; `timespentMs` is the step's
	 * cumulative `_timespent` over all visits (DEV-7864). `extra` adds
	 * top-level fields (DEV-7892); it cannot replace the fields set here.
	 */
	public emitStepCompleted( opts: {
		stepName:          string;
		stepIndex:         number;
		formGroup:         UntypedFormGroup;
		durationMs:        number;
		timespentMs?:      number;
		validationStatus?: 'valid' | 'invalid' | 'skipped';
		locale?:           string;
		gclid?:            string;
		extra?:            Record<string, unknown>;
	} ): void {

		try {

			const payload = {
				...opts.extra,
				form_session_id:   this.getSessionId(),
				client_ts:         new Date().toISOString(),
				step_index:        opts.stepIndex,
				step_name:         opts.stepName,
				step_duration_ms:  Math.max( 0, Math.round( opts.durationMs ) ),
				step_timespent_ms: this.toMs( opts.timespentMs ),
				validation_status: opts.validationStatus ?? 'valid',
				values:            this.extractValues( opts.formGroup ),
				domain:            typeof window !== 'undefined' ? window.location.hostname : '',
				locale:            opts.locale ?? '',
				gclid:             opts.gclid ?? '',
			};

			this.dispatch( payload );

		} catch {
			// Never surface telemetry errors to the form. Drop silently.
		}
	}

	/**
	 * Emit a fire-and-forget event for a discrete user action that is NOT a
	 * forward step-completion (DEV-6453) — e.g. going back to the previous
	 * step, or a post-submit verification button (OTP verify, resend, skip,
	 * change phone).
	 *
	 * Shares the step-event payload shape, so the WP logfile entry keeps its
	 * fields; the action label rides in the `validation_status` field
	 * (forward completions use 'valid'). `step_duration_ms` stays null here:
	 * consumers of the logfile (GetRequestFormLogs) treat a duration as a
	 * completed step. The DEV-7864 extras below reach only the collector.
	 * Never throws.
	 *
	 * Two mutually exclusive ways to attach payload `values`:
	 *   - `formGroup` — snapshot of a FormGroup's values (pre-submission
	 *     steps, back navigation). Never pass the verification FormGroup: its
	 *     OTP code must never leave the browser.
	 *   - `values` — a pre-built object (DEV-6558 post-submit data events, e.g.
	 *     verification `outcome`/`attempt_number`/`resend_count`, or third-party
	 *     answers via `thirdPartyValues()`). Used only
	 *     when no `formGroup` is given.
	 *
	 * Optional DEV-7864 fields, sent only when given: `step_elapsed_ms` (this
	 * visit of the step so far) and `step_timespent_ms` (all visits),
	 * `invalidFields` for a blocked submit click, and `trigger` for the
	 * browser event behind an abandonment.
	 *
	 * `extra` adds top-level fields (DEV-7892: invalid reasons, session
	 * context, timings, …); it cannot replace the fields set here.
	 */
	public emitAction( opts: {
		action:         string;
		stepName:       string;
		stepIndex?:     number;
		formGroup?:     UntypedFormGroup;
		values?:        Record<string, unknown>;
		durationMs?:    number;
		timespentMs?:   number;
		invalidFields?: string[];
		trigger?:       string;
		locale?:        string;
		gclid?:         string;
		extra?:         Record<string, unknown>;
	} ): void {

		try {

			const payload: Record<string, unknown> = {
				...opts.extra,
				form_session_id:   this.getSessionId(),
				client_ts:         new Date().toISOString(),
				step_index:        opts.stepIndex ?? null,
				step_name:         opts.stepName,
				step_duration_ms:  null,
				step_elapsed_ms:   this.toMs( opts.durationMs ),
				step_timespent_ms: this.toMs( opts.timespentMs ),
				validation_status: opts.action,
				values:            opts.formGroup ? this.extractValues( opts.formGroup ) : ( opts.values ?? {} ),
				domain:            typeof window !== 'undefined' ? window.location.hostname : '',
				locale:            opts.locale ?? '',
				gclid:             opts.gclid ?? '',
			};

			if( opts.invalidFields ) {
				payload['invalid_fields'] = opts.invalidFields.slice( 0, TelemetryService.MAX_INVALID_FIELDS );
			}

			if( opts.trigger ) {
				payload['trigger'] = opts.trigger;
			}

			this.dispatch( payload );

		} catch {
			// Never surface telemetry errors to the form. Drop silently.
		}
	}

	/**
	 * Add the envelope every event shares (DEV-7864) and ship the payload via
	 * sendBeacon. No-op when sendBeacon is unavailable (SSR / very old
	 * browsers).
	 *
	 * sendBeacon guarantees neither order nor delivery, so each event carries
	 * a unique `event_uid` (dedupe) and a per-session `event_seq` (order;
	 * a gap is a dropped beacon, not a skipped step).
	 */
	private dispatch( payload: Record<string, unknown> ): void {

		if( typeof navigator === 'undefined' || typeof navigator.sendBeacon !== 'function' ) {
			return;
		}

		const event: Record<string, unknown> = {
			...this.safeContext(),
			...this.environmentContext(),
			...payload,
			event_uid: this.generateUuid(),
			event_seq: this.nextSeq(),
		};

		for( const key of TelemetryService.RESERVED_KEYS ) {
			delete event[ key ];
		}

		// Always a boolean, so WP can tell this bundle (false) from an older one (absent → null).
		event['values_omitted'] = new Blob( [ JSON.stringify( event['values'] ?? {} ) ] ).size > TelemetryService.MAX_VALUES_BYTES;

		if( event['values_omitted'] ) {

			event['values'] = {};
		}

		navigator.sendBeacon( TelemetryService.ENDPOINT_PATH, new Blob( [ JSON.stringify( event ) ], { type: 'application/json' } ) );
	}

	/**
	 * Next per-session sequence number, starting at 1. Kept in sessionStorage
	 * next to the session id, so a reload in the same tab keeps counting up.
	 */
	private nextSeq(): number {

		try {

			const stored = Number( window.sessionStorage.getItem( TelemetryService.SEQ_STORAGE_KEY ) ) || 0;
			const seq = Math.max( stored, this.memorySeq ) + 1;
			window.sessionStorage.setItem( TelemetryService.SEQ_STORAGE_KEY, String( seq ) );
			this.memorySeq = seq;
			return seq;

		} catch {

			this.memorySeq += 1;
			return this.memorySeq;
		}
	}

	/** The registered context, or {} when none is set or it throws. */
	private safeContext(): Record<string, unknown> {

		try {
			return this.contextProvider ? this.contextProvider() : {};
		} catch {
			return {};
		}
	}

	/**
	 * Device and embedding context (DEV-7864). The form runs in an iframe, so
	 * the server's HTTP_REFERER is the iframe's own URL; `parent_url` is the
	 * embedding page. It is readable only on the same origin (Sirelo's own
	 * pages); a partner embed on another domain throws, so it sends '' and
	 * WP stores null (is_iframe true + parent_url null = partner embed). Only
	 * origin and path are sent: the query string can carry the visitor's
	 * address fields (place_from, …) and the click ids travel separately.
	 */
	private environmentContext(): Record<string, unknown> {

		if( typeof window === 'undefined' ) {
			return { app_version: APP_VERSION };
		}

		return {
			viewport:    window.innerWidth + 'x' + window.innerHeight,
			device_type: this.deviceType(),
			is_iframe:   this.isIframe(),
			app_version: APP_VERSION,
			parent_url:  this.parentUrl(),
		};
	}

	/**
	 * Device class from the screen, not the viewport: in an iframe the
	 * viewport is the embed's width. A coarse pointer means touch; a short
	 * screen side under 768px is a phone.
	 */
	private deviceType(): 'mobile' | 'tablet' | 'desktop' {

		const coarse = typeof window.matchMedia === 'function' && window.matchMedia( '(pointer: coarse)' ).matches;
		if( ! coarse ) {
			return 'desktop';
		}

		const shortSide = Math.min( window.screen?.width ?? 0, window.screen?.height ?? 0 );
		return shortSide > 0 && shortSide < 768 ? 'mobile' : 'tablet';
	}

	private isIframe(): boolean {

		try {
			return window.self !== window.top;
		} catch {
			// Reading window.top across origins throws, which only happens in a frame.
			return true;
		}
	}

	/** Origin + path of the embedding page (own page when not framed), or '' across origins. */
	private parentUrl(): string {

		try {
			const location = window.parent.location;
			return location.origin + location.pathname;
		} catch {
			return '';
		}
	}

	/** A non-negative whole number of ms, or null when not given. */
	private toMs( value: number | null | undefined ): number | null {

		return typeof value === 'number' && isFinite( value ) ? Math.max( 0, Math.round( value ) ) : null;
	}

	/**
	 * The step's control values as entered, unmasked (DEV-7892), optionally
	 * only `onlyKeys`. Internal `_` controls are skipped; objects and arrays
	 * are reduced to their shape.
	 */
	private extractValues( formGroup: UntypedFormGroup, onlyKeys?: string[] ): Record<string, unknown> {

		const out: Record<string, unknown> = {};
		const controls = formGroup.controls;

		for( const key of Object.keys( controls ) ) {

			// Skip internal/metadata controls (`_progress`, `_next`, etc.).
			if( key.startsWith( '_' ) || ( onlyKeys && ! onlyKeys.includes( key ) ) ) {
				continue;
			}

			out[ key ] = this.summariseValue( controls[ key ].value );
		}

		return out;
	}

	/**
	 * The third-party questions' answers for the `questions_submitted` event
	 * (DEV-6558), as entered (DEV-7892). `_`-prefixed controls (`_token`,
	 * `_existingKeys`, …) are excluded; objects and arrays are reduced to their
	 * shape.
	 *
	 * `onlyKeys` limits the output to those controls — the questions are
	 * answered one step at a time (DEV-6874), so only the current step's
	 * controls belong in its event.
	 */
	public thirdPartyValues( formGroup: UntypedFormGroup, onlyKeys?: string[] ): Record<string, unknown> {

		return this.extractValues( formGroup, onlyKeys );
	}

	/**
	 * Reduce a non-primitive control value to a small, log-safe shape.
	 * We don't want to dump arbitrary nested objects (e.g. consentMovers'
	 * Mover list) verbatim — keep just the shape so we can spot drop-off.
	 */
	private summariseValue( value: unknown ): unknown {

		if( value === null || value === undefined ) {
			return value;
		}

		const t = typeof value;
		if( t === 'string' || t === 'number' || t === 'boolean' ) {
			return value;
		}

		if( Array.isArray( value ) ) {
			return { _array_length: value.length };
		}

		if( t === 'object' ) {
			return { _object_keys: Object.keys( value as object ).length };
		}

		return null;
	}

	private generateUuid(): string {

		// Native UUID v4 — supported by all evergreen browsers (same floor
		// as navigator.sendBeacon, so no extra compatibility concern).
		const c: any = typeof crypto !== 'undefined' ? crypto : null;
		if( c && typeof c.randomUUID === 'function' ) {
			return c.randomUUID();
		}

		// Fallback using getRandomValues — kept for the unlikely case of
		// older browsers where randomUUID is missing.
		const bytes = new Uint8Array( 16 );
		if( c && typeof c.getRandomValues === 'function' ) {
			c.getRandomValues( bytes );
		} else {
			for( let i = 0; i < 16; i += 1 ) {
				bytes[ i ] = Math.floor( Math.random() * 256 );
			}
		}
		bytes[ 6 ] = ( bytes[ 6 ] & 0x0f ) | 0x40;
		bytes[ 8 ] = ( bytes[ 8 ] & 0x3f ) | 0x80;

		const hex = Array.from( bytes, b => b.toString( 16 ).padStart( 2, '0' ) ).join( '' );
		return `${ hex.slice( 0, 8 ) }-${ hex.slice( 8, 12 ) }-${ hex.slice( 12, 16 ) }-${ hex.slice( 16, 20 ) }-${ hex.slice( 20 ) }`;
	}
}
